import type { SupabaseClient } from '@supabase/supabase-js';

import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

import { ServiceError, type Result } from './errors';

/**
 * Curated review tags (Philippine's design). The set is closed — the submit
 * action validates against it so a client can't write arbitrary tags.
 */
export const REVIEW_TAGS = [
  'As described',
  'Fast shipping',
  'Friendly',
  'Great condition',
  'Smooth chat',
  'Would buy again',
] as const;

export type ReviewTag = (typeof REVIEW_TAGS)[number];

export type SellerReviewRow =
  Database['public']['Tables']['marketplace_seller_reviews']['Row'];

/** Aggregated seller reputation. Zeros when the seller has no reviews. */
export interface SellerStats {
  reviewCount: number;
  avgRating: number;
  avgAsDescribed: number;
}

export interface SubmitReviewInput {
  /** profiles.id of the seller being reviewed. */
  sellerId: string;
  /** profiles.id of the reviewer (= auth.uid(); set by the server action). */
  reviewerId: string;
  /** Overall rating, 1..5. */
  rating: number;
  /** "Item as described" sub-rating, 1..5. Optional. */
  asDescribed?: number;
  /** Subset of REVIEW_TAGS. */
  tags: ReviewTag[];
  /** Free-text comment, <= 2000 chars. Optional. */
  body?: string;
  /** shop_orders.id when reviewing a completed purchase (commerce mode). */
  orderId?: string;
  /** The listing the review is about, for context. Optional. */
  listingId?: string;
}

const MAX_BODY = 2000;

/**
 * Seller reputation for the marketplace. Reads/writes
 * `marketplace_seller_reviews` (RLS: public read, owner-only writes) and the
 * batch `get_marketplace_seller_stats` RPC.
 *
 * `is_verified_purchase` is NEVER written here — a DB trigger derives it from
 * the order linkage so it can't be forged. The submit action enforces who is
 * eligible to review; this service just persists.
 */
export class ReviewService extends BaseService<Database> {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Insert or update the reviewer's single review of a seller. Upserts on the
   * `(reviewer_id, seller_id)` unique pair so a buyer edits their one review
   * rather than stacking duplicates.
   */
  async submitReview(
    input: SubmitReviewInput,
  ): Promise<Result<SellerReviewRow>> {
    if (input.rating < 1 || input.rating > 5) {
      return {
        success: false,
        error: new ServiceError('VALIDATION_FAILED', 'rating must be 1..5'),
      };
    }
    if (
      input.asDescribed !== undefined &&
      (input.asDescribed < 1 || input.asDescribed > 5)
    ) {
      return {
        success: false,
        error: new ServiceError(
          'VALIDATION_FAILED',
          'asDescribed must be 1..5',
        ),
      };
    }
    if (input.reviewerId === input.sellerId) {
      return {
        success: false,
        error: new ServiceError('VALIDATION_FAILED', 'cannot review yourself'),
      };
    }
    const invalidTag = input.tags.find(
      (t) => !REVIEW_TAGS.includes(t as ReviewTag),
    );
    if (invalidTag) {
      return {
        success: false,
        error: new ServiceError(
          'VALIDATION_FAILED',
          `unknown review tag: ${invalidTag}`,
        ),
      };
    }
    const body = input.body?.trim() ?? null;
    if (body && body.length > MAX_BODY) {
      return {
        success: false,
        error: new ServiceError(
          'VALIDATION_FAILED',
          `body exceeds ${MAX_BODY} characters`,
        ),
      };
    }

    const { data, error } = await this.client
      .from('marketplace_seller_reviews')
      .upsert(
        {
          seller_id: input.sellerId,
          reviewer_id: input.reviewerId,
          rating: input.rating,
          as_described: input.asDescribed ?? null,
          tags: input.tags,
          body,
          order_id: input.orderId ?? null,
          listing_id: input.listingId ?? null,
        },
        { onConflict: 'reviewer_id,seller_id' },
      )
      .select()
      .single();

    if (error || !data) {
      return {
        success: false,
        error: new ServiceError(
          'REVIEW_SUBMIT_FAILED',
          error?.message ?? 'failed to submit review',
          error,
        ),
      };
    }
    return { success: true, data: data as SellerReviewRow };
  }

  /**
   * Batch seller stats — ONE RPC for many sellers (no N+1). Sellers with no
   * reviews are simply absent from the returned map; callers treat a miss as
   * zero reviews.
   */
  async getSellerStats(
    sellerIds: string[],
  ): Promise<Result<Map<string, SellerStats>>> {
    const unique = [...new Set(sellerIds)].filter(Boolean);
    if (unique.length === 0) {
      return { success: true, data: new Map() };
    }

    const { data, error } = await this.client.rpc(
      'get_marketplace_seller_stats',
      { p_seller_ids: unique },
    );

    if (error) {
      return {
        success: false,
        error: new ServiceError('STATS_FETCH_FAILED', error.message, error),
      };
    }

    const map = new Map<string, SellerStats>();
    for (const row of data ?? []) {
      map.set(row.seller_id, {
        reviewCount: Number(row.review_count ?? 0),
        avgRating: Number(row.avg_rating ?? 0),
        avgAsDescribed: Number(row.avg_as_described ?? 0),
      });
    }
    return { success: true, data: map };
  }

  /** Stats for a single seller (zeros when none). */
  async getSellerStat(sellerId: string): Promise<Result<SellerStats>> {
    const result = await this.getSellerStats([sellerId]);
    if (!result.success) return result;
    return {
      success: true,
      data: result.data.get(sellerId) ?? {
        reviewCount: 0,
        avgRating: 0,
        avgAsDescribed: 0,
      },
    };
  }

  /** A seller's reviews, newest first, for the reviews drawer. */
  async listSellerReviews(
    sellerId: string,
  ): Promise<Result<SellerReviewRow[]>> {
    const { data, error } = await this.client
      .from('marketplace_seller_reviews')
      .select('*')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false });

    if (error) {
      return {
        success: false,
        error: new ServiceError('REVIEWS_FETCH_FAILED', error.message, error),
      };
    }
    return { success: true, data: (data ?? []) as SellerReviewRow[] };
  }

  /** The reviewer's existing review of a seller (for edit prefill), or null. */
  async getReviewerReview(
    sellerId: string,
    reviewerId: string,
  ): Promise<Result<SellerReviewRow | null>> {
    const { data, error } = await this.client
      .from('marketplace_seller_reviews')
      .select('*')
      .eq('seller_id', sellerId)
      .eq('reviewer_id', reviewerId)
      .maybeSingle();

    if (error) {
      return {
        success: false,
        error: new ServiceError('REVIEW_FETCH_FAILED', error.message, error),
      };
    }
    return { success: true, data: (data as SellerReviewRow | null) ?? null };
  }
}
